import { useState, useCallback, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'

// ── Types ──────────────────────────────────────────────

export type CouponType = 'PARTNER' | 'USER'

export type AdminCoupon = {
  id: string
  code: string
  type: CouponType
  partner_id: string | null
  valid_from: string
  valid_until: string
  partner_commission_pct: number
  member_discount_pct: number
  user_discount_pct: number | null
  max_usage_count: number | null
  current_usage_count: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export type CouponFormData = {
  code: string
  type: CouponType
  valid_from: string
  valid_until: string
  partner_commission_pct: number
  member_discount_pct: number
  user_discount_pct: number | null
  max_usage_count: number | null
  is_active: boolean
}

export const EMPTY_COUPON_FORM: CouponFormData = {
  code: '',
  type: 'PARTNER',
  valid_from: '',
  valid_until: '',
  partner_commission_pct: 0,
  member_discount_pct: 0,
  user_discount_pct: null,
  max_usage_count: null,
  is_active: true,
}

// ── Validation ─────────────────────────────────────────

export const validateCouponForm = (form: CouponFormData): string | null => {
  const code = form.code.trim()
  if (!code) return 'Le code est obligatoire.'
  if (!/^[A-Z0-9_-]{2,30}$/i.test(code))
    return 'Le code doit contenir 2-30 caractères (A-Z, 0-9, -, _).'
  if (!form.valid_from) return 'La date de début est obligatoire.'
  if (!form.valid_until) return 'La date de fin est obligatoire.'
  if (form.valid_from > form.valid_until)
    return 'La date de fin doit être >= date de début.'

  // Validation dynamique selon le type
  if (form.type === 'PARTNER') {
    if (form.member_discount_pct < 0 || form.member_discount_pct > 100)
      return 'La réduction membre doit être entre 0 et 100%.'
    if (form.partner_commission_pct < 0 || form.partner_commission_pct > 100)
      return 'La commission partenaire doit être entre 0 et 100%.'
  }

  if (form.type === 'USER') {
    if (form.user_discount_pct != null && (form.user_discount_pct < 0 || form.user_discount_pct > 100))
      return 'La réduction utilisateur doit être entre 0 et 100%.'
    if (form.partner_commission_pct < 0 || form.partner_commission_pct > 100)
      return 'La commission partenaire doit être entre 0 et 100%.'
  }

  if (form.max_usage_count !== null && form.max_usage_count < 0)
    return "Le nombre max d'utilisations doit être positif ou vide."
  return null
}

// ── Hook ───────────────────────────────────────────────

export const useAdminCoupons = (partnerId: string | null, couponType: CouponType | null = null) => {
  const [coupons, setCoupons] = useState<AdminCoupon[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => setError(null), [])

  // ── List ──────────────────────────────────────────────

  const listCoupons = useCallback(async () => {
    // For PARTNER type, a partner must be selected
    if (!partnerId && couponType !== 'USER') {
      setCoupons([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      let query = supabase
        .from('coupons')
        .select('*')

      if (partnerId) {
        query = query.eq('partner_id', partnerId)
      }

      if (couponType) {
        query = query.eq('type', couponType)
      }

      const { data, error: err } = await query.order('created_at', { ascending: false })

      if (err) throw err
      setCoupons(data ?? [])
    } catch (e: any) {
      setError(e.message ?? 'Erreur lors du chargement des coupons')
    } finally {
      setLoading(false)
    }
  }, [partnerId, couponType])

  useEffect(() => {
    void listCoupons()
  }, [listCoupons])

  // ── Create ────────────────────────────────────────────

  const createCoupon = useCallback(
    async (form: CouponFormData) => {
      setError(null)
      const validationError = validateCouponForm(form)
      if (validationError) {
        setError(validationError)
        return false
      }
      setLoading(true)
      try {
        const { error: err } = await supabase.from('coupons').insert({
          code: form.code.trim().toUpperCase(),
          type: form.type,
          partner_id: partnerId,
          valid_from: form.valid_from,
          valid_until: form.valid_until,
          partner_commission_pct: form.partner_commission_pct,
          member_discount_pct: form.type === 'PARTNER' ? form.member_discount_pct : 0,
          user_discount_pct: form.type === 'USER' ? form.user_discount_pct : null,
          max_usage_count: form.max_usage_count,
          is_active: form.is_active,
        })
        if (err) throw err
        await listCoupons()
        return true
      } catch (e: any) {
        const msg = e.message?.includes('duplicate key')
          ? 'Ce code coupon existe déjà.'
          : e.message ?? 'Erreur lors de la création'
        setError(msg)
        return false
      } finally {
        setLoading(false)
      }
    },
    [partnerId, listCoupons]
  )

  // ── Update ────────────────────────────────────────────

  const updateCoupon = useCallback(
    async (couponId: string, form: CouponFormData) => {
      setError(null)
      const validationError = validateCouponForm(form)
      if (validationError) {
        setError(validationError)
        return false
      }
      setLoading(true)
      try {
        const { error: err } = await supabase
          .from('coupons')
          .update({
            code: form.code.trim().toUpperCase(),
            type: form.type,
            valid_from: form.valid_from,
            valid_until: form.valid_until,
            partner_commission_pct: form.partner_commission_pct,
            member_discount_pct: form.type === 'PARTNER' ? form.member_discount_pct : 0,
            user_discount_pct: form.type === 'USER' ? form.user_discount_pct : null,
            max_usage_count: form.max_usage_count,
            is_active: form.is_active,
            updated_at: new Date().toISOString(),
          })
          .eq('id', couponId)
        if (err) throw err
        await listCoupons()
        return true
      } catch (e: any) {
        const msg = e.message?.includes('duplicate key')
          ? 'Ce code coupon existe déjà.'
          : e.message ?? 'Erreur lors de la mise à jour'
        setError(msg)
        return false
      } finally {
        setLoading(false)
      }
    },
    [listCoupons]
  )

  // ── Toggle status ─────────────────────────────────────

  const toggleStatus = useCallback(
    async (couponId: string, currentlyActive: boolean) => {
      setError(null)
      setLoading(true)
      try {
        const { error: err } = await supabase
          .from('coupons')
          .update({
            is_active: !currentlyActive,
            updated_at: new Date().toISOString(),
          })
          .eq('id', couponId)
        if (err) throw err
        await listCoupons()
        return true
      } catch (e: any) {
        setError(e.message ?? 'Erreur lors du changement de statut')
        return false
      } finally {
        setLoading(false)
      }
    },
    [listCoupons]
  )

  // ── Delete ────────────────────────────────────────────

  const deleteCoupon = useCallback(
    async (couponId: string) => {
      setError(null)

      // Check if coupon has been used (current_usage_count > 0)
      const coupon = coupons.find((c) => c.id === couponId)
      if (coupon && coupon.current_usage_count > 0) {
        setError('Impossible : ce coupon a déjà été utilisé.')
        return false
      }

      setLoading(true)
      try {
        const { error: err } = await supabase
          .from('coupons')
          .delete()
          .eq('id', couponId)
        if (err) throw err
        await listCoupons()
        return true
      } catch (e: any) {
        setError(e.message ?? 'Erreur lors de la suppression')
        return false
      } finally {
        setLoading(false)
      }
    },
    [coupons, listCoupons]
  )

  return {
    coupons,
    loading,
    error,
    clearError,
    listCoupons,
    createCoupon,
    updateCoupon,
    toggleStatus,
    deleteCoupon,
  }
}
